// @ts-check

/**
 * @file Renders a draggable project board task card.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Animated, PanResponder, Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {boardAutomationStatusLabel, translatedAgentState} from "../../../shared/agent-state"
import UserAvatar from "../../../components/user-avatar"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("./local-board-data.js").LocalTaskBoardReadView} TaskBoard */
/** @type {Record<string, object>} */
const styles = {}
export const PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM = 12

/** @typedef {(...args: unknown[]) => unknown} ProjectBoardTaskCardCallback */
/** @typedef {{height: number, width: number, x: number, y: number}} BoardItemLayout */

/**
 * @typedef {object} ProjectBoardTaskCardProps
 * @property {InstanceType<typeof import("../../../models/user.js").default>[]} [assignedUsers] Catalog users assigned to a local task.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardDragEnd Drag-end callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardDragMove Drag-move callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardDragStart Drag-start callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardLayout Layout callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardMenuPress Task menu callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardMoveEnd Task move callback.
 * @property {ProjectBoardTaskCardCallback} onTaskBoardPress Task card callback.
 * @property {number} orderIndex Current index in the rendered column.
 * @property {number} previewOffsetY Live sibling-spacing offset.
 * @property {(taskBoard: TaskBoard) => number} settleOffsetForTaskBoard Stable accessor read at release for the dragged card's final-slot offset.
 * @property {TaskBoard} taskBoard Task board rendered by the card.
 */

/**
 * @typedef {object} ProjectBoardTaskCardState
 * @property {boolean} isDragging Whether the card is currently dragging.
 * @property {boolean} isSettling Whether the card is animating into its final slot.
 */

/**
 * Draggable task card in a project board column.
 * @augments {ShapeComponent<ProjectBoardTaskCardProps, ProjectBoardTaskCardState>}
 */
class ProjectBoardTaskCard extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  static propTypes = PropTypesExact({
    assignedUsers: PropTypes.array,
    onTaskBoardDragEnd: PropTypes.func.isRequired,
    onTaskBoardDragMove: PropTypes.func.isRequired,
    onTaskBoardDragStart: PropTypes.func.isRequired,
    onTaskBoardLayout: PropTypes.func.isRequired,
    onTaskBoardMenuPress: PropTypes.func.isRequired,
    onTaskBoardMoveEnd: PropTypes.func.isRequired,
    onTaskBoardPress: PropTypes.func.isRequired,
    orderIndex: PropTypes.number.isRequired,
    previewOffsetY: PropTypes.number.isRequired,
    settleOffsetForTaskBoard: PropTypes.func.isRequired,
    taskBoard: PropTypes.object.isRequired
  })

  static defaultProps = {
    assignedUsers: undefined
  }
  dragPosition = new Animated.ValueXY()
  dragTransformStyle = {
    transform: this.dragPosition.getTranslateTransform()
  }
  previewOffsetPosition = new Animated.Value(0)
  previewTransformStyle = {
    transform: [
      {
        translateY: this.previewOffsetPosition
      }
    ]
  }
  dragResetTimeout = /** @type {ReturnType<typeof setTimeout> | null} */ null
  draggedRecently = false
  handleDragging = false
  movePanResponder = PanResponder.create({
    onMoveShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: () => {
      this.handleDragging = true
      this.onDragStart()
    },
    onPanResponderMove: Animated.event(
      [
        null,
        {
          dx: this.dragPosition.x,
          dy: this.dragPosition.y
        }
      ],
      {
        useNativeDriver: false,
        listener: (event, gestureState) => {
          this.onDragMove(event, gestureState)
        }
      }
    ),
    onPanResponderRelease: (event, gestureState) => {
      this.handleDragging = false
      this.onDragRelease(event, gestureState)
    },
    onPanResponderTerminate: () => {
      this.handleDragging = false
      this.onDragTerminate()
    },
    onStartShouldSetPanResponder: () => true,
    onStartShouldSetPanResponderCapture: () => true
  })
  /** @type {ProjectBoardTaskCardState} */
  state = {
    isDragging: false,
    isSettling: false
  }

  /** Initializes locale handling. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /** Stops pending timers and animations before the card unmounts. */
  componentWillUnmount() {
    if (this.dragResetTimeout) {
      clearTimeout(this.dragResetTimeout)
      this.dragResetTimeout = null
    }
    this.dragPosition.stopAnimation()
    this.previewOffsetPosition.stopAnimation()
  }

  /**
   * Renders the task card.
   * @returns {ReactNode} Task card content.
   * @throws {Error} When the loaded task is missing an id.
   */
  render() {
    /** @type {TaskBoard} */
    const taskBoard = this.p.taskBoard
    const task = taskBoard.task()
    const taskId = task?.id()
    if (taskId == null) {
      throw new Error(
        `Expected task board ${taskBoard.id()} to have a loaded task with an id`
      )
    }
    const agentState = task?.agentState() || null
    const runtimeStatus = task?.automationRuntimeStatus() || null
    const automationStatus = boardAutomationStatusLabel(agentState, runtimeStatus)
    const assignedUsers = this.p.assignedUsers || []
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const moving = this.s.isDragging || this.s.isSettling
    return (
      <Animated.View
        onLayout={this.tt.onLayout}
        style={[
          (styles[`cardWrapper-${moving}`] ||= {
            marginBottom: PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM,
            position: "relative",
            zIndex: moving ? 5 : 1
          }),
          moving ? this.dragTransformStyle : this.previewTransformStyle
        ]}
        testID={`projectBoardScreen/taskCard/${taskId}`}
      >
        <Pressable
          onPress={this.tt.onCardPress}
          style={styles[`card-${themeName}`] ||= {
            backgroundColor: colors.surface,
            borderColor: colors.border,
            borderRadius: 12,
            borderWidth: 1,
            paddingHorizontal: 14,
            paddingVertical: 12
          }}
          testID={`projectBoardScreen/taskCard/${taskId}/contentButton`}
        >
          <View
            style={styles.header ||= {
              alignItems: "flex-start",
              flexDirection: "row",
              justifyContent: "space-between"
            }}
            testID={`projectBoardScreen/taskCard/${taskId}/header`}
          >
            <View
              style={styles.content ||= {
                flex: 1,
                paddingRight: 12
              }}
              testID={`projectBoardScreen/taskCard/${taskId}/content`}
            >
              <Text
                style={styles[`title-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: 20
                }}
                testID={`projectBoardScreen/taskCard/${taskId}/title`}
              >
                {task?.name() || _("Untitled task")}
              </Text>
              {(agentState || runtimeStatus) &&
                <View
                  style={styles[`agentStatusRow-${themeName}`] ||= {
                    flexDirection: "column",
                    marginTop: 6,
                    rowGap: 4
                  }}
                  testID={`projectBoardScreen/taskCard/${taskId}/agentStatus`}
                >
                  {automationStatus &&
                    <Text
                      style={styles[`automationState-${themeName}`] ||= {
                        color: colors.chipText,
                        fontSize: 11,
                        fontWeight: 600
                      }}
                      testID={`projectBoardScreen/taskCard/${taskId}/agentAutomationState`}
                    >
                      {automationStatus}
                    </Text>
                  }
                  <Text
                    style={styles[`agentState-${themeName}`] ||= {
                      color: colors.textMuted,
                      fontSize: 12,
                      fontWeight: 600,
                      marginTop: 6
                    }}
                    testID={`projectBoardScreen/taskCard/${taskId}/agentState`}
                  >
                    {translatedAgentState(agentState)}
                  </Text>
                </View>
              }
              {assignedUsers.length > 0 &&
                <View
                  style={styles.assignedUsers ||= {
                    flexDirection: "row",
                    gap: 6,
                    marginTop: 10
                  }}
                  testID={`projectBoardScreen/taskCard/${taskId}/assignedUsers`}
                >
                  {assignedUsers.map((user) => {
                    const userId = user.id()
                    const displayName = [user.firstName(), user.lastName()].filter(Boolean).join(" ") || String(userId)
                    return (
                      <UserAvatar
                        displayName={displayName}
                        key={userId}
                        testID={`projectBoardScreen/taskCard/${taskId}/assignedUsers/${userId}`}
                        userId={userId}
                      />
                    )
                  })}
                </View>
              }
            </View>
            <View
              style={styles.actions ||= {
                alignItems: "center",
                flexDirection: "row"
              }}
              testID={`projectBoardScreen/taskCard/${taskId}/actions`}
            >
              <Pressable
                accessibilityLabel={_("More")}
                onPress={this.tt.onMenuPress}
                style={styles[`menuButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.chipBackground,
                  borderRadius: 999,
                  justifyContent: "center",
                  minHeight: 28,
                  minWidth: 28
                }}
                testID={`projectBoardScreen/taskCard/${taskId}/menuButton`}
              >
                <View
                  style={styles.menuButtonIcon ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID={`projectBoardScreen/taskCard/${taskId}/menuButton/icon`}
                >
                  <FontAwesome6 color={colors.chipText} name="ellipsis-vertical" size={14} />
                </View>
              </Pressable>
              <Animated.View
                {...this.movePanResponder.panHandlers}
                accessibilityLabel={_("Move")}
                style={styles[`moveHandle-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.infoBackground,
                  borderRadius: 999,
                  justifyContent: "center",
                  marginLeft: 8,
                  minHeight: 28,
                  minWidth: 28
                }}
                testID={`projectBoardScreen/taskCard/${taskId}/moveHandle`}
              >
                <View
                  style={styles.moveHandleIcon ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID={`projectBoardScreen/taskCard/${taskId}/moveHandle/icon`}
                >
                  <FontAwesome6 color={colors.link} name="arrows-up-down-left-right" size={13} />
                </View>
              </Animated.View>
            </View>
          </View>
        </Pressable>
      </Animated.View>
    )
  }

  /**
   * Animates sibling offset changes when the live drop slot moves.
   * @param {ProjectBoardTaskCardProps} previousProps Props from the previous render.
   * @returns {void} Nothing.
   */
  componentDidUpdate(previousProps) {
    if (previousProps.orderIndex !== this.p.orderIndex) {
      this.previewOffsetPosition.setValue(this.p.previewOffsetY)
      return
    }
    if (
      previousProps.previewOffsetY !== this.p.previewOffsetY &&
      !this.s.isDragging
    ) {
      if (this.s.isSettling) {
        this.stopSettleForPreview(this.p.previewOffsetY)
        return
      }
      this.animatePreviewOffset(this.p.previewOffsetY)
    }
  }

  /** Handles presses on the card body. */
  onCardPress = () => {
    if (this.draggedRecently) {
      return
    }
    this.p.onTaskBoardPress(this.p.taskBoard)
  }

  /** Opens the task board menu. */
  onMenuPress = () => {
    this.p.onTaskBoardMenuPress(this.p.taskBoard)
  }

  /** Starts card dragging. */
  onDragStart = () => {
    if (this.dragResetTimeout) {
      clearTimeout(this.dragResetTimeout)
      this.dragResetTimeout = null
    }
    this.draggedRecently = true
    this.previewOffsetPosition.setValue(0)
    this.dragPosition.setOffset({
      x: 0,
      y: 0
    })
    this.dragPosition.setValue({
      x: 0,
      y: 0
    })
    this.s.isDragging = true
    this.p.onTaskBoardDragStart(this.p.taskBoard)
  }

  /**
   * Handles drag movement for live board spacing.
   * @param {object} event Pan responder event.
   * @param {{moveX?: number, moveY?: number}} gestureState Coordinates used to update the target slot.
   * @returns {void} Nothing.
   */
  onDragMove = (event, gestureState) => {
    this.p.onTaskBoardDragMove(this.p.taskBoard, event, gestureState)
  }

  /**
   * Finishes a drag gesture.
   * @param {object} event Pan responder event.
   * @param {{dx: number, dy: number}} gestureState Gesture delta state.
   * @returns {void} Nothing.
   */
  onDragRelease = (event, gestureState) => {
    // Read the settle offset lazily at release so the dragged card never takes
    // a per-move-changing prop, which would re-render the active PanResponder
    // host mid-gesture and terminate the drag.
    const settleOffsetY = this.p.settleOffsetForTaskBoard(this.p.taskBoard)
    this.dragPosition.flattenOffset()
    this.s.isDragging = false
    this.s.isSettling = true
    void this.p.onTaskBoardMoveEnd(this.p.taskBoard, event, gestureState)
    this.dragPosition.setValue({
      x: gestureState.dx || 0,
      y: (gestureState.dy || 0) - settleOffsetY
    })
    Animated.spring(this.dragPosition, {
      toValue: {
        x: 0,
        y: 0
      },
      useNativeDriver: false
    }).start(() => {
      this.s.isSettling = false
      this.p.onTaskBoardDragEnd(this.p.taskBoard)
      this.scheduleDraggedRecentlyReset()
    })
  }

  /** Cancels card dragging. */
  onDragTerminate = () => {
    this.dragPosition.flattenOffset()
    this.s.isDragging = false
    this.s.isSettling = false
    this.p.onTaskBoardDragEnd(this.p.taskBoard)
    Animated.spring(this.dragPosition, {
      toValue: {
        x: 0,
        y: 0
      },
      useNativeDriver: false
    }).start()
    this.scheduleDraggedRecentlyReset()
  }

  /**
   * Reports the rendered task-card slot height.
   * @param {{nativeEvent?: {layout?: BoardItemLayout}}} event React Native layout payload.
   * @returns {void} Nothing.
   */
  onLayout = (event) => {
    const layout = layoutFromEvent(event)
    if (layout) {
      this.p.onTaskBoardLayout(
        this.p.taskBoard,
        layout.height + PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM,
        layout
      )
    }
  }

  /**
   * Animates this card into a live preview offset.
   * @param {number} previewOffsetY Target preview offset.
   * @returns {void} Nothing.
   */
  animatePreviewOffset(previewOffsetY) {
    Animated.spring(this.previewOffsetPosition, {
      toValue: previewOffsetY,
      useNativeDriver: false
    }).start()
  }

  /**
   * Ends a release animation so this card can move as another card's sibling.
   * @param {number} previewOffsetY Target preview offset.
   * @returns {void} Nothing.
   */
  stopSettleForPreview(previewOffsetY) {
    this.dragPosition.stopAnimation((currentValue) => {
      const currentY =
        typeof currentValue?.y === "number" ? currentValue.y : 0
      this.previewOffsetPosition.setValue(currentY)
      this.dragPosition.setValue({
        x: 0,
        y: 0
      })
      this.s.isSettling = false
      this.scheduleDraggedRecentlyReset()
      this.animatePreviewOffset(previewOffsetY)
    })
  }

  /** Clears the post-drag click guard shortly after movement ends. */
  scheduleDraggedRecentlyReset() {
    if (this.dragResetTimeout) {
      clearTimeout(this.dragResetTimeout)
    }
    this.dragResetTimeout = setTimeout(() => {
      this.draggedRecently = false
      this.dragResetTimeout = null
    }, 200)
  }
}

/** @type {NamedExoticComponent<ProjectBoardTaskCardProps>} */
const ProjectBoardTaskCardComponent = memo(shapeComponent(ProjectBoardTaskCard))

export default ProjectBoardTaskCardComponent

/**
 * Reads a React Native layout payload.
 * @param {{nativeEvent?: {layout?: BoardItemLayout}}} event Native event carrying dimensions.
 * @returns {BoardItemLayout | null} Layout value, if complete.
 */
function layoutFromEvent(event) {
  const layout = event.nativeEvent?.layout
  if (
    !layout ||
    typeof layout.height !== "number" ||
    typeof layout.width !== "number" ||
    typeof layout.x !== "number" ||
    typeof layout.y !== "number"
  ) {
    return null
  }
  return layout
}
